import React, { useEffect, useState } from 'react';
import { API_BASE } from './../../config';
import { useAuth } from './../../context/AuthContext';
import ApiKeyCard from '../../components/ApiKeyCard';
import './ApiKeysTab.css';

const ApiKeysTab = ({ tenantId }) => {
  const { token } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/admin/api-key/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load keys (${res.status})`);
      const data = await res.json();
      setKeys(data);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this key?')) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/admin/api-key/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to revoke key (${res.status})`);
      setMessage({ type: 'success', text: 'Key revoked successfully.' });
      await fetchKeys();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const copyKey = (value) => {
    navigator.clipboard.writeText(value);
    setMessage({ type: 'success', text: 'API key copied to clipboard!' });
  };

  const regenerate = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/admin/api-key/${tenantId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to regenerate key (${res.status})`);
      const newKey = await res.json();
      setMessage({ type: 'success', text: `New API Key generated: ${newKey.key}` });
      await fetchKeys();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) fetchKeys();
  }, [tenantId]);

  return (
    <section className="apikeys-tab" aria-label="API Keys management">
      <div className="apikeys-header">
        <h3>API Keys</h3>
        <button
          onClick={regenerate}
          disabled={loading}
          aria-busy={loading}
          aria-disabled={loading}
          type="button"
        >
          {loading ? 'Processing...' : 'Regenerate Key'}
        </button>
      </div>

      {message && (
        <div
          role="alert"
          className={`message-banner ${message.type === 'error' ? 'error' : 'success'}`}
          onAnimationEnd={() => setMessage(null)}
        >
          {message.text}
        </div>
      )}

      <div className="apikeys-list">
        {keys.length === 0 && !loading && (
          <p className="no-keys">No API keys found for this tenant.</p>
        )}

        {keys.map(k => (
          <ApiKeyCard
            key={k.id}
            keyData={k}
            onCopy={copyKey}
            onRevoke={revokeKey}
            loading={loading}
          />
        ))}
      </div>
    </section>
  );
};

export default ApiKeysTab;
